import { runningUnderNativePlatform } from 'src/store/running-under-native-platform';

function headerValue(headers: HeadersInit | undefined, name: string): string | undefined {
  if (!headers) {
    return undefined;
  }
  const lowerCaseName = name.toLowerCase();
  if (headers instanceof Headers) {
    return headers.get(name) ?? undefined;
  }
  if (Array.isArray(headers)) {
    const hit = headers.find(([k]) => k.toLowerCase() === lowerCaseName);
    return hit?.[1];
  }
  const hit = Object.entries(headers).find(([k]) => k.toLowerCase() === lowerCaseName);
  return typeof hit?.[1] === 'string' ? hit[1] : undefined;
}

function authModeFromHeaders(headers: HeadersInit | undefined): 'basic' | 'bearer' | 'none' | 'unknown' {
  const authorization = headerValue(headers, 'Authorization');
  if (!authorization) {
    return 'none';
  }
  if (authorization.startsWith('Basic ')) {
    return 'basic';
  }
  if (authorization.startsWith('Bearer ')) {
    return 'bearer';
  }
  return 'unknown';
}

function isWriteMethod(method: string): boolean {
  return method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
}

function diagnosticsContext(url: string, method: string, headers: HeadersInit | undefined) {
  const isNative = runningUnderNativePlatform();
  const authMode = authModeFromHeaders(headers);
  // Native tokens are stored in SecureStorage; localStorage is only accurate on web.
  const hasAccessTokenInLocalStorage = !!localStorage.getItem('access_token');
  const hasLegacyToken = !!localStorage.getItem('dabih-token');
  return {
    url,
    method,
    authMode,
    hasAccessTokenInLocalStorage,
    tokenStorage: isNative ? 'secure-storage' : 'local-storage',
    hasLegacyToken,
    timestamp: Date.now(),
  };
}

export function logWriteRequestDiagnostics(url: string, method: string, headers: HeadersInit | undefined): void {
  if (!isWriteMethod(method)) {
    return;
  }

  const authMode = authModeFromHeaders(headers);
  // Request logs are only relevant when legacy/basic auth is used.
  if (authMode !== 'basic') {
    return;
  }

  console.info('[auth-diagnostics] write-request', diagnosticsContext(url, method, headers));
}

export function logWriteResponseDiagnostics(
  url: string,
  method: string,
  status: number,
  headers: HeadersInit | undefined,
): void {
  if (!isWriteMethod(method)) {
    return;
  }

  const authMode = authModeFromHeaders(headers);
  // Response logs are relevant for failures, and for basic-auth usage even on 2xx.
  if (status < 400 && authMode !== 'basic') {
    return;
  }

  console.info('[auth-diagnostics] write-response', {
    ...diagnosticsContext(url, method, headers),
    status,
  });
}

export function logWriteErrorDiagnostics(
  url: string,
  method: string,
  status: number | undefined,
  headers: HeadersInit | undefined,
): void {
  if (!isWriteMethod(method)) {
    return;
  }

  // Error logs are always relevant because this path only runs for thrown failures.
  console.info('[auth-diagnostics] write-response-error', {
    ...diagnosticsContext(url, method, headers),
    status,
  });
}
